import React, { useEffect, useState } from "react";
import { placeholder } from "src/assets/images";
import { Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "src/components/Navbar";
import Footer from "src/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { getItems, getmaterial } from "src/api";
import Modal from "src/components/modal/Modal";
import FilterShop from "src/components/modal/FilterShop";
import Loader from "src/components/Loader";
import { useProtectedRoutesContext } from "src/context/ProtectedRoutes";
import { renderCurrency } from "src/components/renderCurrency";

const Shop = () => {
    const [search, setSearch] = useState(""); // For search input
    const [selectedMaterial, setSelectedMaterial] = useState(""); // For material filter
    const [selectedGrade, setSelectedGrade] = useState(""); // For grade filter (if applicable)

    const navigate = useNavigate();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const { conversionRates } = useProtectedRoutesContext()

    // Fetch materials
    const { data: material, isLoading } = useQuery({
        queryKey: ["getmaterial"],
        queryFn: () => getmaterial(),
    });

    const params = {
        search: search.trim() ? search : "",
        materialId: selectedMaterial || undefined,
        gradeId: selectedGrade || undefined,
    };


    if (selectedMaterial) {
        params.materialId = selectedMaterial;
    } else if (selectedGrade) {
        params.gradeId = selectedGrade;
    } else {
        params.search = "";
    }

    const { data: items, refetch } = useQuery({
        queryKey: ["getItems", search, selectedMaterial, selectedGrade], // Ensure search is in the key
        queryFn: () => {
            const params = {
                search: search.trim() ? search : "",
                materialId: selectedMaterial || undefined,
                gradeId: selectedGrade || undefined,
            };
            return getItems(params);
        },
        enabled: true,
    });

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            refetch();
        }, 500); // Wait 500ms before firing

        return () => clearTimeout(delaySearch); // Cleanup on unmount
    }, [search, selectedMaterial, selectedGrade, refetch]);


    if (isLoading) {
        return <Loader />;
    }
    return (
        <div className="relative">
            <Navbar />
            <div className="container mx-auto py-10">
                <section className="text-center mb-6">
                    <h1 className="text-3xl font-bold">Bringing Bold Style to Life</h1>
                    <h6 className="text-gray-600 mt-2">Fabrics That Inspire Your Creative Vision, More Than Just Fabric — A Story Behind Every Piece</h6>
                </section>
                {/* Filter Section */}
                <div className="flex justify-between items-center mb-6 the-product-search-input-container">
                    {/* Filter Button */}
                    <div className="flex items-center gap-2 fatix-silver-val" onClick={() => setIsEditModalOpen(true)}>
                        <Filter className="w-5 h-5" />
                        <span className="text-lg font-medium">Filter</span>
                    </div>

                    {/* Search Input */}
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search products..."
                        className="bg-gray-200 px-4 py-2 rounded-md border border-gray-300 the-product-search-input"
                    />

                    {/* Dropdown for Material Filtering */}
                    <select
                        value={selectedMaterial}
                        onChange={(e) => setSelectedMaterial(e.target.value)}
                        className="bg-gray-200 px-4 py-2 rounded-md border border-gray-300 fatix-input-patterns"
                    >
                        <option value="">Select Material</option>
                        {material?.data?.map((mat) => (
                            <option key={mat.id} value={mat.id}>
                                {mat.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Product Grid */}
                {items?.data?.length > 0 ?
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {items?.data?.map((product) => {
                            const imageUrl = product.itemImages?.length > 0
                                ? product.itemImages[0].url
                                : placeholder;
                            return (
                                <div>

                                    <Card key={product.id} className="cursor-pointer fatix-card-shop-container"
                                        onClick={() => {
                                            navigate(`/product/${product.id}`);
                                            window.scrollTo(0, 0);
                                        }}
                                    >
                                        <CardHeader className="p-0">
                                            <img src={imageUrl} alt={product.name} className="w-full h-[320px] object-cover" />
                                        </CardHeader>
                                        <CardContent className="flex justify-between p-0 mt-2">
                                            <CardTitle className="fatix-product-title">{product.design_number}</CardTitle>
                                            <span className="text-lg font-semibold fatix-product-title">{renderCurrency(product?.grade?.price * conversionRates[product?.materialId])}</span>
                                        </CardContent>
                                    </Card>
                                </div>
                            );
                        })}
                    </div>

                    : <div className="no-result-container">
                        <p>No Product for this Filter</p>
                    </div>
                }
            </div>
            <Footer />
            <Modal isOpen={isEditModalOpen} setIsOpen={setIsEditModalOpen} title="Filter Shop" size="large">
                <FilterShop
                    setIsOpen={setIsEditModalOpen}
                    selectedMaterial={selectedMaterial}
                    selectedGrade={selectedGrade}
                    setSelectedGrade={setSelectedGrade} // Pass setSelectedGrade to FilterShop
                />
            </Modal>
        </div>
    );
};

export default Shop;
